import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Save, Wallet, CreditCard, ShoppingCart, TrendingUp, Target } from "lucide-react";
import { toast } from "sonner";
import { useVat } from "@/context/VatContext";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";

const API = process.env.REACT_APP_BACKEND_URL;

const formatR = (v) => {
  const n = parseFloat(v) || 0;
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// "cash" = safe / paid in cash; "eft" = bank account / paid by EFT
export const methodOf = (item, kind) =>
  item.method || (kind === "source" ? (/bank|eft/i.test(item.name || "") ? "eft" : "cash") : "eft");

const MethodToggle = ({ item, kind, labels, onChange, testId }) => {
  const m = methodOf(item, kind);
  return (
    <button
      type="button"
      onClick={() => onChange({ ...item, method: m === "cash" ? "eft" : "cash" })}
      title="Click to switch between cash and EFT/bank"
      data-testid={testId}
      className={`shrink-0 w-12 rounded-full border px-1.5 py-0.5 text-[10px] font-bold transition-colors ${m === "cash" ? "border-amber-400 bg-amber-100 text-amber-800" : "border-sky-400 bg-sky-100 text-sky-800"}`}
    >
      {m === "cash" ? labels[0] : labels[1]}
    </button>
  );
};

const EditableRow = ({ item, onChange, onRemove, nameLabel = "Description", kind, methodLabels, testId }) => (
  <div className="flex items-center gap-2 group">
    {methodLabels && <MethodToggle item={item} kind={kind} labels={methodLabels} onChange={onChange} testId={testId} />}
    <Input
      value={item.name}
      onChange={(e) => onChange({ ...item, name: e.target.value })}
      placeholder={nameLabel}
      className="flex-1 h-9 text-sm"
    />
    <div className="flex items-center gap-1 w-40">
      <span className="text-sm text-gray-500 shrink-0">R</span>
      <Input
        type="number"
        step="0.01"
        value={item.amount || ""}
        onChange={(e) => onChange({ ...item, amount: e.target.value })}
        placeholder="0.00"
        className="h-9 text-sm text-right font-mono"
      />
    </div>
    <button
      onClick={onRemove}
      className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600 shrink-0"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

const SectionCard = ({ title, icon: Icon, color, items, setItems, nameLabel, children, kind, methodLabels }) => {
  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const addRow = () => setItems([...items, { name: "", amount: "" }]);
  const updateRow = (idx, val) => { const c = [...items]; c[idx] = val; setItems(c); };
  const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));

  return (
    <Card className="flex flex-col">
      <CardHeader className={`${color} border-b py-3 px-4`}>
        <CardTitle className="flex items-center gap-2 text-sm font-bold">
          <Icon className="h-4 w-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 flex-1 flex flex-col gap-2">
        {children}
        <div className="space-y-1.5 flex-1">
          {items.map((item, idx) => (
            <EditableRow
              key={idx}
              item={item}
              onChange={(v) => updateRow(idx, v)}
              onRemove={() => removeRow(idx)}
              nameLabel={nameLabel}
              kind={kind}
              methodLabels={methodLabels}
              testId={methodLabels ? `position-${kind}-method-${idx}` : undefined}
            />
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={addRow} className="w-full text-xs mt-1 border border-dashed">
          <Plus className="h-3 w-3 mr-1" /> Add Row
        </Button>
        <div className={`mt-auto pt-2 border-t flex justify-between items-center font-bold text-sm`}>
          <span>TOTAL:</span>
          <span className="font-mono text-base">{formatR(total)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const DayIncomeRow = ({ day, amount, isSunday, isLocked, suggested, onChange }) => (
  <div className={`flex items-center gap-2 ${isSunday ? "opacity-40" : ""}`}>
    <span className={`text-xs w-8 text-right shrink-0 ${isSunday ? "text-red-400 font-semibold" : "text-gray-500"}`}>
      {day}{isSunday ? " S" : ""}
    </span>
    <div className="flex items-center gap-1 flex-1">
      <span className="text-xs text-gray-400">R</span>
      <Input
        type="number"
        step="0.01"
        value={isSunday ? "0" : (amount || "")}
        onChange={(e) => !isSunday && onChange(e.target.value)}
        placeholder={isSunday ? "Sunday" : (suggested ? suggested.toFixed(0) : "0.00")}
        disabled={isSunday}
        className={`h-7 text-xs text-right font-mono ${isLocked ? "bg-green-50 border-green-300" : ""} ${isSunday ? "bg-gray-100" : ""}`}
      />
    </div>
    {isLocked && <span className="text-green-600 text-xs shrink-0">actual</span>}
  </div>
);

export const CurrentPositionTab = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [cashPosition, setCashPosition] = useState([]);
  const [accountsToPay, setAccountsToPay] = useState([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [stockToBuy, setStockToBuy] = useState([]);
  const [projectedIncome, setProjectedIncome] = useState([]);

  // Income Statement budget data — fetched alongside Current Position so the
  // Net Profit Target → daily sales calc stays in sync with the IS budgets.
  const [isBudget, setIsBudget] = useState({
    net_profit_target: 0,
    budget_revenue: 0,            // excl VAT
    budget_cogs: 0,                // excl VAT
    budget_total_expenses: 0,      // excl-VAT operating expenses (informational excluded)
    actual_revenue_mtd: 0,         // excl VAT
  });
  const { vatRate } = useVat();

  const monthKey = `${year}-${String(month).padStart(2, "0")}`;
  const daysInMonth = new Date(year, month, 0).getDate();

  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/financial-summary/current-position/${monthKey}`);
      setCashPosition(data.cash_position?.length ? data.cash_position : [{ name: "Cash in Safe", amount: "" }, { name: "In Bank Account", amount: "" }]);
      setAccountsToPay(data.accounts_to_pay?.length ? data.accounts_to_pay : [{ name: "", amount: "" }]);
      setMonthlyExpenses(data.monthly_expenses?.length ? data.monthly_expenses : [{ name: "", amount: "" }]);
      setStockToBuy(data.stock_to_buy?.length ? data.stock_to_buy : [{ name: "", amount: "" }]);

      if (data.projected_income?.length) {
        // Merge saved data with full month — fill missing days
        const full = Array.from({ length: daysInMonth }, (_, i) => {
          const saved = data.projected_income.find(d => d.day === i + 1);
          return { day: i + 1, amount: saved?.amount || "", locked: saved?.locked || false };
        });
        setProjectedIncome(full);
      } else {
        setProjectedIncome(Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, amount: "", locked: false })));
      }
    } catch {
      setCashPosition([{ name: "Cash in Safe", amount: "" }, { name: "In Bank Account", amount: "" }]);
      setAccountsToPay([{ name: "", amount: "" }]);
      setMonthlyExpenses([{ name: "", amount: "" }]);
      setStockToBuy([{ name: "", amount: "" }]);
      setProjectedIncome(Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, amount: "", locked: false })));
    } finally {
      setLoading(false);
    }
  }, [monthKey, daysInMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  // Fetch Income Statement budget snapshot for the same month so we can compute
  // the daily sales target that hits the Net Profit Target.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/income-statement/report`,
          { params: { year, month, view: "single" } }
        );
        if (cancelled) return;
        const m = data?.months?.[month] || {};
        setIsBudget({
          net_profit_target: parseFloat(m.budget_net_profit_target) || 0,
          budget_revenue: parseFloat(m.budget_revenue) || 0,
          budget_cogs: parseFloat(m.budget_cogs) || 0,
          budget_total_expenses: parseFloat(m.budget_total_expenses) || 0,
          actual_revenue_mtd: parseFloat(m.revenue) || 0,
        });
      } catch {
        if (!cancelled) {
          setIsBudget({
            net_profit_target: 0, budget_revenue: 0, budget_cogs: 0,
            budget_total_expenses: 0, actual_revenue_mtd: 0,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [year, month]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveWithRetry(() =>
        axios.put(`${API}/api/financial-summary/current-position/${monthKey}`, {
          cash_position: cashPosition,
          accounts_to_pay: accountsToPay,
          monthly_expenses: monthlyExpenses,
          stock_to_buy: stockToBuy,
          projected_income: projectedIncome,
        }, { timeout: 30000 })
      );
      toast.success(`Saved current position for ${monthNames[month - 1]} ${year}`);
    } catch (e) {
      // Surface the actual backend error + log full context for debugging.
      console.error("Save Position failed", e);
      toast.error(errorMessage(e, "Failed to save position"));
    } finally {
      setSaving(false);
    }
  };

  // Totals
  const cashTotal = cashPosition.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const payTotal = accountsToPay.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const expenseTotal = monthlyExpenses.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const outgoingTotal = payTotal + expenseTotal;
  const buyTotal = stockToBuy.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  // Sunday detection helper
  const isSunday = (day) => new Date(year, month - 1, day).getDay() === 0;

  // Target = What I still need to earn to cover all bills
  // = (Bills to pay + Stock to buy) - Cash available
  const targetNeeded = Math.max(0, outgoingTotal + buyTotal - cashTotal);

  // Count remaining open business days (not Sunday, not locked/actual)
  // "Locked" days are days where actual sales have already been recorded (historical)
  const openBusinessDays = projectedIncome.filter(d => !d.locked && !isSunday(d.day));
  
  // Daily target = Target needed / remaining open days
  // This is the amount you need to earn EACH remaining day to cover all bills
  const dailySuggested = openBusinessDays.length > 0 ? targetNeeded / openBusinessDays.length : 0;

  // Calculate the total of actual (locked) days for display purposes
  const lockedDays = projectedIncome.filter(d => d.locked && !isSunday(d.day));
  const lockedTotal = lockedDays.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);

  // Calculate actual total including suggestions for display
  const incomeTotal = projectedIncome.reduce((s, d) => {
    if (isSunday(d.day)) return s;
    if (d.locked) return s + (parseFloat(d.amount) || 0);
    return s + (parseFloat(d.amount) || dailySuggested);
  }, 0);
  const netPosition = cashTotal + incomeTotal - outgoingTotal - buyTotal;
  const sumBy = (rows, kind, m) => rows.filter((r) => methodOf(r, kind) === m).reduce((t, r) => t + (parseFloat(r.amount) || 0), 0);
  const cashOnHand = sumBy(cashPosition, "source", "cash");
  const bankBalance = sumBy(cashPosition, "source", "eft");
  const payCash = sumBy(accountsToPay, "pay", "cash") + sumBy(monthlyExpenses, "pay", "cash");
  const payEft = sumBy(accountsToPay, "pay", "eft") + sumBy(monthlyExpenses, "pay", "eft");

  // ============================================================
  // PROFIT-DRIVEN DAILY SALES TARGET (from Income Statement)
  // ============================================================
  // Two figures, both using the same GP% and budgeted operating expenses
  // from the Income Statement:
  //   1. TARGET   sales/day → ends the month with the Net Profit Target
  //   2. SURVIVAL sales/day → ends the month at break-even (R 0 profit)
  // Both incl-VAT so they match till totals.
  const budgetGpMargin = isBudget.budget_revenue > 0
    ? (isBudget.budget_revenue - isBudget.budget_cogs) / isBudget.budget_revenue
    : 0;
  const vatMul = 1 + (vatRate || 0);

  const computeDaily = (profitTarget) => {
    if (budgetGpMargin <= 0) return { reqIncl: 0, perDay: 0 };
    const reqExcl = (profitTarget + isBudget.budget_total_expenses) / budgetGpMargin;
    const reqIncl = reqExcl * vatMul;
    const remainingExcl = Math.max(0, reqExcl - isBudget.actual_revenue_mtd);
    const remainingIncl = remainingExcl * vatMul;
    const perDay = openBusinessDays.length > 0 ? remainingIncl / openBusinessDays.length : 0;
    return { reqIncl, perDay };
  };

  const targetFig = computeDaily(isBudget.net_profit_target);
  const survivalFig = computeDaily(0);
  const mtdIncl = isBudget.actual_revenue_mtd * vatMul;
  const profitTargetActive = (
    isBudget.net_profit_target !== 0 &&
    budgetGpMargin > 0
  );
  const survivalActive = budgetGpMargin > 0 && isBudget.budget_total_expenses > 0;

  // ============================================================
  // COMBINED "REAL" TARGET — covers accounting profit AND cash-flow gap
  // ============================================================
  // Target #1 says "these are the sales needed to hit your net profit
  // target on paper" — but that assumes bills get paid on time. If the
  // store is behind on supplier accounts + expenses (Accounts to Pay >
  // Cash Available), the till has to bring in enough physical cash to
  // close that gap AS WELL AS hit the profit target. This target
  // combines both: whichever remaining incl-VAT sales figure is higher.
  const baselineFig = profitTargetActive ? targetFig : survivalFig;
  const remainingForBaseline = Math.max(0, baselineFig.reqIncl - mtdIncl);
  const remainingForCashGap = targetNeeded; // already = bills − cash
  const combinedRemainingIncl = Math.max(remainingForBaseline, remainingForCashGap);
  const combinedReqIncl = mtdIncl + combinedRemainingIncl;
  const combinedPerDay = openBusinessDays.length > 0
    ? combinedRemainingIncl / openBusinessDays.length
    : 0;
  // Only show the combined target when the cash gap actually pushes the
  // number higher than the profit-target baseline — otherwise it would
  // just duplicate #1 or #2.
  const combinedActive = (
    (profitTargetActive || survivalActive) &&
    targetNeeded > 0 &&
    remainingForCashGap > remainingForBaseline + 0.01
  );

  const updateDayIncome = (dayIdx, value) => {
    const c = [...projectedIncome];
    // If user enters a value, lock it as "actual"
    c[dayIdx] = { ...c[dayIdx], amount: value, locked: value !== "" && value !== null };
    setProjectedIncome(c);
  };

  const clearDay = (dayIdx) => {
    const c = [...projectedIncome];
    c[dayIdx] = { ...c[dayIdx], amount: "", locked: false };
    setProjectedIncome(c);
  };

  const autoFillAll = () => {
    setProjectedIncome(prev => prev.map(d => {
      if (isSunday(d.day)) return { ...d, amount: "", locked: false };
      if (d.locked) return d;
      return { ...d, amount: dailySuggested > 0 ? dailySuggested.toFixed(0) : "", locked: false };
    }));
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="current-position-tab">
      {/* Month Selector & Save */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(+e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(+e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-position-btn">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Position"}
        </Button>
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
          <div className="text-xs text-green-700">Cash Available</div>
          <div className="text-lg font-bold font-mono text-green-800">{formatR(cashTotal)}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <div className="text-xs text-red-700">Accounts to Pay</div>
          <div className="text-lg font-bold font-mono text-red-800">{formatR(outgoingTotal)}</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
          <div className="text-xs text-orange-700">Stock to Buy</div>
          <div className="text-lg font-bold font-mono text-orange-800">{formatR(buyTotal)}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
          <div className="text-xs text-blue-700">Projected Income</div>
          <div className="text-lg font-bold font-mono text-blue-800">{formatR(incomeTotal)}</div>
        </div>
        <div className={`${netPosition >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"} border rounded-lg p-3 text-center`}>
          <div className={`text-xs ${netPosition >= 0 ? "text-emerald-700" : "text-red-700"}`}>Net Position</div>
          <div className={`text-lg font-bold font-mono ${netPosition >= 0 ? "text-emerald-800" : "text-red-800"}`}>{formatR(netPosition)}</div>
        </div>
      </div>

      {/* Cash vs EFT split */}
      <div className="grid gap-3 md:grid-cols-2" data-testid="position-method-split">
        {[
          { key: "cash", label: "CASH: Safe vs suppliers/expenses paid in cash", have: cashOnHand, pay: payCash, haveLabel: "Cash in safe", tone: "amber" },
          { key: "eft", label: "EFT: Bank vs suppliers/expenses paid by EFT", have: bankBalance, pay: payEft, haveLabel: "In bank", tone: "sky" },
        ].map((b) => {
          const left = b.have - b.pay;
          return (
            <div key={b.key} className={`rounded-lg border p-3 ${b.tone === "amber" ? "border-amber-200 bg-amber-50" : "border-sky-200 bg-sky-50"}`} data-testid={`position-split-${b.key}`}>
              <div className={`text-xs font-bold ${b.tone === "amber" ? "text-amber-800" : "text-sky-800"}`}>{b.label}</div>
              <div className="mt-1 grid grid-cols-3 gap-2 text-sm">
                <div><div className="text-[11px] text-gray-500">{b.haveLabel}</div><div className="font-mono font-semibold">{formatR(b.have)}</div></div>
                <div><div className="text-[11px] text-gray-500">Still to pay</div><div className="font-mono font-semibold text-red-700" data-testid={`position-split-${b.key}-pay`}>{formatR(b.pay)}</div></div>
                <div><div className="text-[11px] text-gray-500">{left >= 0 ? "Left over" : "Short"}</div><div className={`font-mono font-bold ${left >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid={`position-split-${b.key}-left`}>{formatR(left)}</div></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 4-Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Column 1: Cash Position */}
        <SectionCard
          title="CASH IN SAFE / BANK"
          icon={Wallet}
          color="bg-green-50"
          items={cashPosition}
          setItems={setCashPosition}
          nameLabel="Source"
          kind="source"
          methodLabels={["SAFE", "BANK"]}
        />

        {/* Column 2: Accounts to Pay (Suppliers + Expenses) */}
        <Card className="flex flex-col">
          <CardHeader className="bg-red-50 border-b py-3 px-4">
            <CardTitle className="flex items-center gap-2 text-sm font-bold">
              <CreditCard className="h-4 w-4" /> STILL HAVE TO PAY
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 flex-1 flex flex-col gap-2">
            {/* Supplier Accounts */}
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Supplier Accounts</div>
            <div className="space-y-1.5">
              {accountsToPay.map((item, idx) => (
                <EditableRow
                  key={`a-${idx}`}
                  item={item}
                  onChange={(v) => { const c = [...accountsToPay]; c[idx] = v; setAccountsToPay(c); }}
                  onRemove={() => setAccountsToPay(accountsToPay.filter((_, i) => i !== idx))}
                  nameLabel="Supplier"
                  kind="pay"
                  methodLabels={["CASH", "EFT"]}
                  testId={`position-supplier-method-${idx}`}
                />
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAccountsToPay([...accountsToPay, { name: "", amount: "" }])} className="w-full text-xs border border-dashed">
              <Plus className="h-3 w-3 mr-1" /> Add Supplier
            </Button>
            <div className="pt-2 border-t flex justify-between items-center text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-mono font-semibold">{formatR(payTotal)}</span>
            </div>

            {/* Monthly Expenses */}
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-2">Monthly Expenses</div>
            <div className="space-y-1.5">
              {monthlyExpenses.map((item, idx) => (
                <EditableRow
                  key={`e-${idx}`}
                  item={item}
                  onChange={(v) => { const c = [...monthlyExpenses]; c[idx] = v; setMonthlyExpenses(c); }}
                  onRemove={() => setMonthlyExpenses(monthlyExpenses.filter((_, i) => i !== idx))}
                  nameLabel="Expense"
                  kind="pay"
                  methodLabels={["CASH", "EFT"]}
                  testId={`position-expense-method-${idx}`}
                />
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setMonthlyExpenses([...monthlyExpenses, { name: "", amount: "" }])} className="w-full text-xs border border-dashed">
              <Plus className="h-3 w-3 mr-1" /> Add Expense
            </Button>
            <div className="pt-2 border-t flex justify-between items-center text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-mono font-semibold">{formatR(expenseTotal)}</span>
            </div>
            <div className="pt-2 border-t space-y-0.5 text-xs">
              <div className="flex justify-between"><span className="text-amber-800 font-semibold">Pay in CASH:</span><span className="font-mono">{formatR(payCash)}</span></div>
              <div className="flex justify-between"><span className="text-sky-800 font-semibold">Pay by EFT:</span><span className="font-mono">{formatR(payEft)}</span></div>
            </div>
            <div className="pt-2 border-t flex justify-between items-center font-bold text-sm">
              <span>TOTAL:</span>
              <span className="font-mono text-base text-red-700">{formatR(outgoingTotal)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Column 3: Stock to Buy */}
        <SectionCard
          title="STILL WANT TO BUY"
          icon={ShoppingCart}
          color="bg-orange-50"
          items={stockToBuy}
          setItems={setStockToBuy}
          nameLabel="Supplier"
        />

        {/* Column 4: Projected Daily Income */}
        <Card className="flex flex-col">
          <CardHeader className="bg-blue-50 border-b py-3 px-4">
            <CardTitle className="flex items-center gap-2 text-sm font-bold">
              <TrendingUp className="h-4 w-4" /> PROJECTED INCOME
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 flex-1 flex flex-col gap-1">
            {/* === Two clear daily-sales targets, both incl-VAT === */}
            {profitTargetActive && (
              <div className="bg-emerald-50 border-2 border-emerald-400 rounded p-2 text-xs mb-1" data-testid="profit-target-block">
                <div className="flex items-center gap-1 font-bold text-emerald-900">
                  <Target className="h-3 w-3" /> 1. To hit {formatR(isBudget.net_profit_target)} NET PROFIT
                </div>
                <div className="text-emerald-800 mt-0.5 leading-snug">
                  Sales needed this month: <strong>{formatR(targetFig.reqIncl)}</strong> (incl VAT)<br/>
                  Less MTD sales so far: −{formatR(mtdIncl)}<br/>
                  {openBusinessDays.length} days left =&nbsp;
                  <strong className="text-emerald-900 text-sm">{formatR(targetFig.perDay)}/day</strong>
                </div>
              </div>
            )}
            {!profitTargetActive && (
              <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs mb-1 text-gray-500 italic">
                Set a Net Profit Target on Income Statement → Enter Budget to see your target daily sales here.
              </div>
            )}

            {survivalActive && (
              <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs mb-1" data-testid="survival-target-block">
                <div className="flex items-center gap-1 font-bold text-amber-900">
                  <TrendingUp className="h-3 w-3" /> 2. To just BREAK EVEN (cover expenses, R 0 profit)
                </div>
                <div className="text-amber-800 mt-0.5 leading-snug">
                  Sales needed this month: <strong>{formatR(survivalFig.reqIncl)}</strong> (incl VAT)<br/>
                  Less MTD sales so far: −{formatR(mtdIncl)}<br/>
                  {openBusinessDays.length} days left =&nbsp;
                  <strong className="text-amber-900 text-sm">{formatR(survivalFig.perDay)}/day</strong>
                </div>
              </div>
            )}

            {(profitTargetActive || survivalActive) && (
              <div className="text-[10px] text-gray-500 italic px-1 mb-1">
                Both use GP {Math.round(budgetGpMargin * 100)}% and your budgeted expenses from the Income Statement.
                The two numbers are <strong>not added together</strong> — aim for {combinedActive ? "#3" : "#1"}.
              </div>
            )}

            {/* Combined "REAL" target — only shows when the outstanding cash
                gap (bills + stock − cash) demands more remaining sales than
                the profit target alone. This is the true "cover everything
                for the month" figure the shop owner actually needs. */}
            {combinedActive && (
              <div className="bg-purple-50 border-2 border-purple-500 rounded p-2 text-xs mb-1" data-testid="combined-target-block">
                <div className="flex items-center gap-1 font-bold text-purple-900">
                  <Target className="h-3 w-3" /> 3. To cover ALL bills AND hit profit target
                </div>
                <div className="text-purple-800 mt-0.5 leading-snug">
                  Sales needed this month: <strong>{formatR(combinedReqIncl)}</strong> (incl VAT)<br/>
                  Less MTD sales so far: −{formatR(mtdIncl)}<br/>
                  {openBusinessDays.length} days left =&nbsp;
                  <strong className="text-purple-900 text-sm">{formatR(combinedPerDay)}/day</strong>
                </div>
                <div className="text-[10px] text-purple-700 mt-1">
                  Higher of {profitTargetActive ? "target #1" : "break-even #2"} and the outstanding cash gap ({formatR(targetNeeded)}). This
                  is the number to aim for if you want every supplier paid and every wage covered by month-end.
                </div>
              </div>
            )}

            {/* Cash-flow sanity check — compares the NEW cash coming in
                from hitting profit target #1 against the actual cash gap
                (bills + stock-to-buy − cash on hand). If the remaining
                sales won't cover the shortfall we say so plainly — this
                used to unconditionally read "covers this comfortably"
                regardless of the numbers, which was misleading. */}
            {targetNeeded > 0 && (() => {
              // "New cash in" from hitting the highest active target — if
              // the combined target is showing, that's the one that
              // actually closes the gap; else fall back to #1 or #2.
              const activeReqIncl = combinedActive
                ? combinedReqIncl
                : (profitTargetActive ? targetFig.reqIncl : survivalFig.reqIncl);
              const activeLabel = combinedActive
                ? "#3"
                : (profitTargetActive ? "#1" : "#2");
              const incomingCash = Math.max(0, activeReqIncl - mtdIncl);
              const shortfall = targetNeeded - incomingCash;
              const covers = incomingCash >= targetNeeded - 0.01;

              if (covers) {
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded p-1.5 text-[11px] mb-1" data-testid="cash-bills-block">
                    <span className="text-blue-700">
                      Cash sanity check — bills due this month exceed cash by{" "}
                      <strong>{formatR(targetNeeded)}</strong>. Hitting {activeLabel} above brings in{" "}
                      <strong>{formatR(incomingCash)}</strong>, which covers it comfortably.
                    </span>
                  </div>
                );
              }
              return (
                <div className="bg-red-50 border border-red-300 rounded p-1.5 text-[11px] mb-1" data-testid="cash-bills-block">
                  <span className="text-red-800">
                    ⚠ Cash gap this month: <strong>{formatR(targetNeeded)}</strong>. Even if you hit target{" "}
                    {activeLabel}, only <strong>{formatR(incomingCash)}</strong> of new cash comes in —
                    you&apos;ll still be short by <strong>{formatR(shortfall)}</strong>. Consider negotiating
                    extended payment terms with suppliers or lifting the sales target.
                  </span>
                </div>
              );
            })()}
            <div className="flex gap-1 mb-1">
              <Button variant="outline" size="sm" onClick={autoFillAll} className="flex-1 text-xs h-7" data-testid="auto-fill-btn">
                Auto-fill open days
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setProjectedIncome(prev => prev.map(d => ({ ...d, amount: "", locked: false })))} className="text-xs h-7 text-red-500">
                Clear all
              </Button>
            </div>
            <div className="space-y-1 flex-1 max-h-[480px] overflow-y-auto pr-1">
              {projectedIncome.map((d, idx) => (
                <DayIncomeRow
                  key={idx}
                  day={d.day}
                  amount={d.amount}
                  isSunday={isSunday(d.day)}
                  isLocked={d.locked}
                  suggested={dailySuggested}
                  onChange={(v) => updateDayIncome(idx, v)}
                />
              ))}
            </div>
            <div className="mt-auto pt-2 border-t flex justify-between items-center font-bold text-sm">
              <span>TOTAL:</span>
              <span className="font-mono text-base text-blue-700">{formatR(incomeTotal)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
